import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Maximize } from 'lucide-react';
import { MediaFile } from '../types';

interface ImageSliderProps {
  images: MediaFile[];
}

const ImageSlider: React.FC<ImageSliderProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showFullscreen, setShowFullscreen] = useState(false);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setZoom(1);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(3, prev + 0.25));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.5, prev - 0.25));
  };

  const currentImage = images[currentIndex];

  if (!currentImage) return null;

  return (
    <div className="w-full h-full flex flex-col bg-black">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 md:p-4 bg-black bg-opacity-50 absolute top-0 left-0 right-0 z-10">
        <div className="flex items-center gap-3 text-white">
          <span className="text-xs md:text-sm">
            {currentIndex + 1} de {images.length}
          </span>
        </div>
        
        <div className="flex items-center gap-1 md:gap-2">
          <button
            onClick={handleZoomOut}
            className="p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <ZoomOut size={14} className="md:w-4 md:h-4" />
          </button>
          
          <span className="text-white text-xs md:text-sm min-w-8 md:min-w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          
          <button
            onClick={handleZoomIn}
            className="p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <ZoomIn size={14} className="md:w-4 md:h-4" />
          </button>
          
          <button
            onClick={() => setShowFullscreen(true)}
            className="p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <Maximize size={14} className="md:w-4 md:h-4" />
          </button>
          
          <button
            onClick={() => window.open(currentImage.url, '_blank')}
            className="p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <Download size={14} className="md:w-4 md:h-4" />
          </button>
        </div>
      </div>

      {/* Main Image */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img
          src={currentImage.url}
          alt={currentImage.file_name}
          className="max-w-full max-h-full object-contain transition-transform duration-300 protected-media"
          style={{ transform: `scale(${zoom})` }}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          draggable={false}
        />
        <div className="watermark-overlay"></div>
        <div className="security-overlay"></div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all z-10"
            >
              <ChevronLeft size={20} className="md:w-6 md:h-6" />
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all z-10"
            >
              <ChevronRight size={20} className="md:w-6 md:h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="p-2 md:p-4 bg-black bg-opacity-50">
          <div className="flex gap-1 md:gap-2 justify-center overflow-x-auto">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setZoom(1);
                }}
                className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-white' 
                    : 'border-transparent hover:border-gray-400'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.file_name}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image Info */}
      <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 bg-black bg-opacity-70 text-white p-2 md:p-3 rounded-lg">
        <div className="text-xs md:text-sm">
          <p className="font-medium truncate max-w-[200px] md:max-w-none">{currentImage.file_name}</p>
          {currentImage.width && currentImage.height && (
            <p className="text-gray-300">
              {currentImage.width} × {currentImage.height} • {(currentImage.file_size / 1024 / 1024).toFixed(1)} MB
            </p>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {showFullscreen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={() => setShowFullscreen(false)}
            className="absolute top-2 right-2 md:top-4 md:right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
          >
            ✕
          </button>
          
          <img
            src={currentImage.url}
            alt={currentImage.file_name}
            className="max-w-full max-h-full object-contain"
          />
          
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all"
              >
                <ChevronLeft size={20} className="md:w-6 md:h-6" />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all"
              >
                <ChevronRight size={20} className="md:w-6 md:h-6" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;